import { useState, useEffect } from 'react';
import { getCustomersWithUsers, createCustomer, updateCustomer, deleteCustomer, getProducts, getCoupons, getSales } from '../lib/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users, UserPlus, Copy, Package, Ticket, KeyRound, Tag, RotateCcw } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialFormData = {
  code: '',
  name: '',
  contact_person: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  route_number: '',
  saturday_route_number: '',
  is_pin_entry: false,
  visible_products: [],
  favorite_products: [],
  favorite_sales: [],
  available_coupons: [],
  double_invoice: false,
  double_invoice_customer_code: '',
};

const initialAccountData = {
  loginId: '',
  password: '',
};

export const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState({});
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  const [resetResult, setResetResult] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [accountData, setAccountData] = useState(initialAccountData);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, productsRes, salesRes, couponsRes] = await Promise.all([
        getCustomersWithUsers(), 
        getProducts(),
        getSales(),
        getCoupons()
      ]);
      
      // 顧客データにユーザー情報が含まれている
      const customersData = customersRes.data;
      setCustomers(customersData);
      setProducts(productsRes.data);
      setSales(salesRes.data.filter(s => s.is_active));
      setCoupons(couponsRes.data.filter(c => c.is_active));
      
      // ユーザーマップを構築（既存のコードとの互換性のため）
      const usersMap = {};
      for (const customer of customersData) {
        usersMap[customer.id] = customer.users || [];
      }
      setCustomerUsers(usersMap);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setSelectedCustomer(null);
    setFormData(initialFormData);
    setDialogOpen(true);
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      code: customer.code,
      name: customer.name,
      contact_person: customer.contact_person || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      notes: customer.notes || '',
      route_number: customer.route_number || '',
      saturday_route_number: customer.saturday_route_number || '',
      is_pin_entry: customer.is_pin_entry || false,
      visible_products: customer.visible_products || [],
      favorite_products: customer.favorite_products || [],
      favorite_sales: customer.favorite_sales || [],
      available_coupons: customer.available_coupons || [],
      double_invoice: customer.double_invoice || false,
      double_invoice_customer_code: customer.double_invoice_customer_code || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = (customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const handleAddAccount = (customer) => {
    setSelectedCustomer(customer);
    setAccountData({
      loginId: customer.code || '',  // 取引先コードをデフォルトに
      password: '',
    });
    setAccountDialogOpen(true);
  };

  const handleEditProducts = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      favorite_products: customer.favorite_products || customer.visible_products || [],
    });
    setProductDialogOpen(true);
  };

  const handleEditSales = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      ...formData,
      favorite_sales: customer.favorite_sales || [],
    });
    setSalesDialogOpen(true);
  };

  const handleEditCoupons = (customer) => {
    setSelectedCustomer(customer);
    setCouponDialogOpen(true);
  };

  // クーポンのステータスを取得（閲覧用）
  const getCouponStatus = (couponId, customerId) => {
    const coupon = coupons.find(c => c.id === couponId);
    if (!coupon) return { status: 'unknown', label: '不明' };
    
    // 使用済みかチェック（used_countは全体のカウントなので、実際には注文データから確認が必要）
    // ここでは単回使用制限と使用回数で判断
    if (!coupon.is_active) return { status: 'inactive', label: '無効' };
    
    const today = new Date().toISOString().split('T')[0];
    if (coupon.expiration_date && coupon.expiration_date < today) {
      return { status: 'expired', label: '期限切れ' };
    }
    
    return { status: 'active', label: '有効' };
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      toast.error('必須項目を入力してください');
      return;
    }

    setSaving(true);
    try {
      if (selectedCustomer) {
        await updateCustomer(selectedCustomer.id, formData);
        toast.success('取引先を更新しました');
      } else {
        await createCustomer(formData);
        toast.success('取引先を登録しました');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'エラーが発生しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProducts = async () => {
    setSaving(true);
    try {
      await updateCustomer(selectedCustomer.id, { favorite_products: formData.favorite_products });
      toast.success('お気に入り商品を更新しました');
      setProductDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'エラーが発生しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSales = async () => {
    setSaving(true);
    try {
      await updateCustomer(selectedCustomer.id, { favorite_sales: formData.favorite_sales });
      toast.success('お気に入り特売を更新しました');
      setSalesDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'エラーが発生しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAccount = async () => {
    if (!accountData.loginId || !accountData.password) {
      toast.error('ログインIDとパスワードを入力してください');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/customer/auth/register`, {
        login_id: accountData.loginId,
        password: accountData.password,
        customer_id: selectedCustomer.id,
      });
      toast.success('顧客アカウントを作成しました');
      setAccountDialogOpen(false);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'エラーが発生しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteCustomer(selectedCustomer.id);
      toast.success('取引先を削除しました');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  // 削除された顧客を検索
  const searchDeletedCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/customers/deleted/search`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeletedCustomers(res.data);
      setRestoreDialogOpen(true);
      if (res.data.length === 0) {
        toast.info('復元可能な顧客が見つかりませんでした');
      }
    } catch (error) {
      toast.error('検索に失敗しました');
    }
  };

  // 顧客を復元
  const restoreCustomer = async (customer) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/customers/restore`, customer, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${customer.name || '顧客'}を復元しました`);
      setRestoreDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('復元に失敗しました');
    }
  };

  const copyLoginUrl = () => {
    const url = `${window.location.origin}/customer/login`;
    navigator.clipboard.writeText(url);
    toast.success('ログインURLをコピーしました');
  };

  const handleResetPassword = (customer, user) => {
    setSelectedCustomer(customer);
    setSelectedUser(user);
    setResetResult(null);
    setNewPassword('');
    setPasswordResetDialogOpen(true);
  };

  const confirmResetPassword = async () => {
    if (!newPassword.trim()) {
      toast.error('新しいパスワードを入力してください');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/customers/${selectedCustomer.id}/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword
      });
      setResetResult(res.data);
      toast.success('パスワードを変更しました');
      fetchData(); // 顧客データを再取得してパスワード表示を更新
    } catch (error) {
      const message = error.response?.data?.detail || 'パスワードリセットに失敗しました';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const copyPassword = (password) => {
    navigator.clipboard.writeText(password);
    toast.success('パスワードをコピーしました');
  };

  const toggleProduct = (productId) => {
    const current = formData.favorite_products || [];
    if (current.includes(productId)) {
      setFormData({ ...formData, favorite_products: current.filter(id => id !== productId) });
    } else {
      setFormData({ ...formData, favorite_products: [...current, productId] });
    }
  };

  const selectAllProducts = () => {
    setFormData({ ...formData, favorite_products: products.map(p => p.id) });
  };

  const clearAllProducts = () => {
    setFormData({ ...formData, favorite_products: [] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="customers-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">取引先</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={searchDeletedCustomers} className="border-amber-400 text-amber-600 hover:bg-amber-50">
            <RotateCcw className="h-4 w-4 mr-2" />
            削除した顧客を復元
          </Button>
          <Button variant="outline" onClick={copyLoginUrl}>
            <Copy className="h-4 w-4 mr-2" />
            顧客ログインURL
          </Button>
          <Button onClick={handleAdd} data-testid="add-customer-btn">
            <Plus className="h-4 w-4 mr-2" />
            新規登録
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="取引先名・コードで検索..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredCustomers.length === 0 ? (
            <div className="empty-state py-12">
              <Users className="h-12 w-12 text-slate-300 mb-3" />
              <p>取引先がありません</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="customers-table">
                <thead>
                  <tr>
                    <th>コード</th>
                    <th>ルート</th>
                    <th>取引先名</th>
                    <th>担当者</th>
                    <th className="hidden md:table-cell">メール</th>
                    <th className="hidden md:table-cell">電話番号</th>
                    <th>AI提案</th>
                    <th>クーポン</th>
                    <th>顧客アカウント</th>
                    <th className="text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => {
                    const users = customerUsers[customer.id] || [];
                    const favoriteProductCount = customer.favorite_products?.length || customer.visible_products?.length || 0;
                    const favoriteSalesCount = customer.favorite_sales?.length || 0;
                    const couponCount = customer.available_coupons?.length || 0;
                    return (
                      <tr key={customer.id}>
                        <td className="font-mono">{customer.code}</td>
                        <td>
                          {customer.route_number || '-'}
                          {customer.saturday_route_number && (
                            <span className="text-xs text-orange-600 ml-1">(土:{customer.saturday_route_number})</span>
                          )}
                        </td>
                        <td className="font-medium">
                          {customer.name}
                          {customer.double_invoice && (
                            <Badge className="ml-2 bg-amber-100 text-amber-800 text-xs">W伝票→{customer.double_invoice_customer_code}</Badge>
                          )}
                        </td>
                        <td>{customer.contact_person || '-'}</td>
                        <td className="hidden md:table-cell">{customer.email || '-'}</td>
                        <td className="hidden md:table-cell">{customer.phone || '-'}</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProducts(customer)}
                              data-testid={`edit-products-${customer.id}`}
                            >
                              <Package className="h-3 w-3 mr-1" />
                              {favoriteProductCount > 0 ? `商品${favoriteProductCount}件` : '商品未設定'}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditSales(customer)}
                              className={favoriteSalesCount > 0 ? 'border-red-300 text-red-600' : ''}
                              data-testid={`edit-sales-${customer.id}`}
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {favoriteSalesCount > 0 ? `特売${favoriteSalesCount}件` : '特売未設定'}
                            </Button>
                          </div>
                        </td>
                        <td>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditCoupons(customer)}
                            data-testid={`edit-coupons-${customer.id}`}
                          >
                            <Ticket className="h-3 w-3 mr-1" />
                            {couponCount > 0 ? `${couponCount}件` : '未設定'}
                          </Button>
                        </td>
                        <td>
                          {users.length > 0 ? (
                            <div className="space-y-1">
                              {users.map((u) => (
                                <div key={u.id} className="flex flex-col gap-1 p-2 bg-slate-50 rounded text-xs">
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-500">ID:</span>
                                    <span className="font-mono">{u.email}</span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => copyPassword(u.email)}
                                      className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600"
                                    >
                                      <Copy className="h-3 w-3" />
                                    </Button>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-500">PW:</span>
                                    <span className="font-mono">{u.password_plain || '(未設定)'}</span>
                                    {u.password_plain && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => copyPassword(u.password_plain)}
                                        className="h-5 w-5 p-0 text-slate-400 hover:text-slate-600"
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResetPassword(customer, u)}
                                    className="h-6 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 mt-1"
                                    data-testid={`reset-password-${u.id}`}
                                  >
                                    <KeyRound className="h-3 w-3 mr-1" />
                                    PW再発行
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddAccount(customer)}
                              data-testid={`add-account-${customer.id}`}
                            >
                              <UserPlus className="h-3 w-3 mr-1" />
                              アカウント作成
                            </Button>
                          )}
                        </td>
                        <td className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(customer)}
                            data-testid={`edit-customer-${customer.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(customer)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-customer-${customer.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>{selectedCustomer ? '取引先編集' : '取引先登録'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>取引先コード *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="CUST-001"
                data-testid="customer-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>ルート番号</Label>
              <Input
                value={formData.route_number}
                onChange={(e) => setFormData({ ...formData, route_number: e.target.value })}
                placeholder="R-001"
                data-testid="customer-route-input"
              />
            </div>
            <div className="space-y-2">
              <Label>土曜日ルート番号</Label>
              <Input
                value={formData.saturday_route_number}
                onChange={(e) => setFormData({ ...formData, saturday_route_number: e.target.value })}
                placeholder="空欄＝通常と同じ"
                data-testid="customer-saturday-route-input"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_pin_entry || false}
                  onChange={(e) => setFormData({ ...formData, is_pin_entry: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">PIN入力用</span>
              </label>
            </div>
            <div className="space-y-2">
              <Label>取引先名 *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="株式会社サンプル"
                data-testid="customer-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>担当者</Label>
              <Input
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="山田 太郎"
                data-testid="customer-contact-input"
              />
            </div>
            <div className="space-y-2">
              <Label>メールアドレス</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contact@example.com"
                data-testid="customer-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>電話番号</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="03-1234-5678"
                data-testid="customer-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>住所</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="東京都渋谷区..."
                data-testid="customer-address-input"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>備考</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="メモなど"
                rows={3}
                data-testid="customer-notes-input"
              />
            </div>
            {/* ダブル伝票設定 */}
            <div className="space-y-3 md:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="double-invoice"
                  checked={formData.double_invoice}
                  onCheckedChange={(checked) => setFormData({ ...formData, double_invoice: checked })}
                  data-testid="double-invoice-checkbox"
                />
                <Label htmlFor="double-invoice" className="font-medium cursor-pointer">ダブル伝票</Label>
                <span className="text-xs text-slate-500">（同じ内容を別の顧客名でも作成）</span>
              </div>
              {formData.double_invoice && (
                <div className="space-y-1 pl-6">
                  <Label className="text-sm">請求先の顧客コード</Label>
                  <Input
                    value={formData.double_invoice_customer_code}
                    onChange={(e) => setFormData({ ...formData, double_invoice_customer_code: e.target.value })}
                    placeholder="顧客コードを入力"
                    className="w-48"
                    data-testid="double-invoice-code-input"
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-customer-btn">
              {saving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>⭐ お気に入り商品の設定</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              「{selectedCustomer?.name}」のお気に入り商品を選択してください。<br />
              <span className="text-slate-400">※ 顧客は「お気に入り」タブで素早くアクセスできます</span><br />
              <span className="text-slate-400">※ 全商品は「すべて」タブで引き続き閲覧可能です</span>
            </p>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={selectAllProducts}>全て選択</Button>
              <Button variant="outline" size="sm" onClick={clearAllProducts}>全て解除</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-3">
              {products.length === 0 ? (
                <p className="text-center text-slate-400 py-4">商品がありません</p>
              ) : (
                products.map((product) => (
                  <div key={product.id} className="flex items-center space-x-3 py-2 border-b last:border-b-0">
                    <Checkbox
                      id={`product-${product.id}`}
                      checked={formData.favorite_products?.includes(product.id)}
                      onCheckedChange={() => toggleProduct(product.id)}
                    />
                    <label htmlFor={`product-${product.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium">{product.name}</span>
                      {product.origin && <span className="text-sm text-slate-500 ml-2">{product.origin}</span>}
                      <span className="text-sm text-slate-400 ml-2">¥{product.price?.toLocaleString()}</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {formData.favorite_products?.length > 0 
                ? `⭐ ${formData.favorite_products.length}件選択中` 
                : '未選択'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSaveProducts} disabled={saving} data-testid="save-products-btn">
              {saving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales Selection Dialog */}
      <Dialog open={salesDialogOpen} onOpenChange={setSalesDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="sales-dialog">
          <DialogHeader>
            <DialogTitle>🏷️ お気に入り特売の設定</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              「{selectedCustomer?.name}」のお気に入り特売を選択してください。<br />
              <span className="text-slate-400">※ 「AI提案」タブに表示されます</span>
            </p>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, favorite_sales: sales.map(s => s.id) })}>全て選択</Button>
              <Button variant="outline" size="sm" onClick={() => setFormData({ ...formData, favorite_sales: [] })}>全て解除</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-3">
              {sales.length === 0 ? (
                <p className="text-center text-slate-400 py-4">有効な特売がありません</p>
              ) : (
                sales.map((sale) => (
                  <div key={sale.id} className="flex items-center space-x-3 py-2 border-b last:border-b-0">
                    <Checkbox
                      id={`sale-${sale.id}`}
                      checked={formData.favorite_sales?.includes(sale.id)}
                      onCheckedChange={() => {
                        const current = formData.favorite_sales || [];
                        if (current.includes(sale.id)) {
                          setFormData({ ...formData, favorite_sales: current.filter(id => id !== sale.id) });
                        } else {
                          setFormData({ ...formData, favorite_sales: [...current, sale.id] });
                        }
                      }}
                    />
                    <label htmlFor={`sale-${sale.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium text-red-600">{sale.name}</span>
                      {sale.origin && <span className="text-sm text-slate-500 ml-2">{sale.origin}</span>}
                      <span className="text-sm text-red-500 ml-2">¥{sale.price?.toLocaleString()}</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {formData.favorite_sales?.length > 0 
                ? `🏷️ ${formData.favorite_sales.length}件選択中` 
                : '未選択'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalesDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSaveSales} disabled={saving} data-testid="save-sales-btn">
              {saving ? '保存中...' : '保存'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Account Dialog */}
      <Dialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader>
            <DialogTitle>顧客アカウント作成</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              「{selectedCustomer?.name}」のログインアカウントを作成します。
            </p>
            <div className="space-y-2">
              <Label>ログインID *</Label>
              <Input
                type="text"
                value={accountData.loginId}
                onChange={(e) => setAccountData({ ...accountData, loginId: e.target.value })}
                placeholder="例: CUST-001"
                data-testid="account-login-id-input"
              />
              <p className="text-xs text-slate-400">※ メールアドレス以外でも登録可能です</p>
            </div>
            <div className="space-y-2">
              <Label>パスワード *</Label>
              <Input
                type="text"
                value={accountData.password}
                onChange={(e) => setAccountData({ ...accountData, password: e.target.value })}
                placeholder="初期パスワード"
                data-testid="account-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccountDialogOpen(false)}>キャンセル</Button>
            <Button onClick={handleSaveAccount} disabled={saving} data-testid="save-account-btn">
              {saving ? '作成中...' : 'アカウント作成'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Coupon View Dialog (閲覧専用) */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle>クーポン状況</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-4">
              「{selectedCustomer?.name}」に配布されているクーポン
            </p>
            <p className="text-xs text-slate-400 mb-4">
              ※ クーポンの配布は「クーポン管理」画面から行ってください
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-3">
              {(() => {
                const customerCoupons = coupons.filter(c => 
                  (selectedCustomer?.available_coupons || []).includes(c.id)
                );
                if (customerCoupons.length === 0) {
                  return <p className="text-center text-slate-400 py-4">配布されているクーポンがありません</p>;
                }
                return customerCoupons.map((coupon) => {
                  const status = getCouponStatus(coupon.id, selectedCustomer?.id);
                  return (
                    <div key={coupon.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                      <div className="flex-1">
                        <span className="font-medium">{coupon.name}</span>
                        <span className="text-sm text-slate-500 ml-2">
                          {coupon.coupon_type === 'percentage' && `${coupon.value}% OFF`}
                          {coupon.coupon_type === 'fixed_amount' && `¥${coupon.value.toLocaleString()} OFF`}
                          {coupon.coupon_type === 'free_sample' && `${coupon.value}個 無料`}
                        </span>
                        {coupon.expiration_date && (
                          <span className="text-xs text-slate-400 ml-2">〜{coupon.expiration_date}</span>
                        )}
                      </div>
                      <Badge variant={status.status === 'active' ? 'default' : 'secondary'}>
                        {status.label}
                      </Badge>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponDialogOpen(false)}>閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordResetDialogOpen} onOpenChange={setPasswordResetDialogOpen}>
        <DialogContent data-testid="password-reset-dialog">
          <DialogHeader>
            <DialogTitle>パスワード再発行</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {!resetResult ? (
              <>
                <p className="text-sm text-slate-500">
                  「{selectedCustomer?.name}」のアカウント
                </p>
                <p className="font-medium">{selectedUser?.email}</p>
                <div>
                  <Label>新しいパスワード</Label>
                  <Input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="新しいパスワードを入力"
                    data-testid="new-password-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    ※ お好きなパスワードを設定できます
                  </p>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-emerald-600 font-medium">
                  パスワードを変更しました
                </p>
                <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                  <div>
                    <p className="text-xs text-slate-500">ログインID</p>
                    <p className="font-medium">{resetResult.login_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">新しいパスワード</p>
                    <div className="flex items-center gap-2">
                      <p className="font-mono text-2xl font-bold text-blue-600 tracking-widest">{resetResult.temp_password}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyPassword(resetResult.temp_password)}
                        data-testid="copy-password-btn"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        コピー
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-400">
                  ※ このパスワードを顧客にお伝えください
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            {!resetResult ? (
              <>
                <Button variant="outline" onClick={() => setPasswordResetDialogOpen(false)}>キャンセル</Button>
                <Button 
                  onClick={confirmResetPassword} 
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="confirm-reset-btn"
                >
                  {saving ? 'リセット中...' : 'パスワードをリセット'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setPasswordResetDialogOpen(false)} data-testid="close-reset-dialog-btn">
                閉じる
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>取引先を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{selectedCustomer?.name}」を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Deleted Customers Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-amber-600" />
              削除した顧客を復元
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {deletedCustomers.length === 0 ? (
              <p className="text-slate-500 text-center py-4">
                復元可能な顧客が見つかりませんでした。<br />
                <span className="text-sm">（注文履歴がある顧客のみ復元できます）</span>
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {deletedCustomers.map((customer, idx) => (
                  <div key={customer.id} className="p-3 border rounded-lg bg-slate-50 space-y-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <div>
                          <Label className="text-xs text-slate-500">顧客名</Label>
                          <Input
                            value={customer.name || ''}
                            onChange={(e) => {
                              const updated = [...deletedCustomers];
                              updated[idx] = { ...customer, name: e.target.value };
                              setDeletedCustomers(updated);
                            }}
                            placeholder="顧客名を入力"
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">顧客コード</Label>
                          <Input
                            value={customer.code || ''}
                            onChange={(e) => {
                              const updated = [...deletedCustomers];
                              updated[idx] = { ...customer, code: e.target.value };
                              setDeletedCustomers(updated);
                            }}
                            placeholder="コードを入力"
                            className="h-8 text-sm"
                          />
                        </div>
                        <p className="text-xs text-slate-400">
                          注文数: {customer.order_count || 0}件 | ID: {customer.id.slice(0, 8)}...
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => restoreCustomer(customer)}
                        className="bg-amber-500 hover:bg-amber-600 ml-3"
                        disabled={!customer.name}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        復元
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-400 text-center">
              顧客名を入力して「復元」ボタンをクリックしてください。
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
